import { memo, useEffect } from 'react';

import { Box, Divider, Flex, Grid, GridItem } from '@chakra-ui/react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChevronRight,
  getInitialsFromName,
  Icon,
  ListItem,
  ListItemText,
  ProfileCard,
  ProfileCardItem,
  ProfileCardKey,
  ProfileCardValue,
  Role,
} from '@mesh.ai/rabbit';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import { getApplicableL2MyTeamModules } from 'Deer/components/navigation/secondary/MyTeam/MyTeamModules';
import { usePeople } from 'Deer/components/navigation/secondary/MyTeam/People/hooks/usePeople';
import { STATIC_COHORTS } from 'Deer/constants/cohorts';
import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS, staticRoutes } from 'Deer/constants/routes';
import { getProfileRole, getUserAccess } from 'Deer/helpers/getUserAccess';
import { useGetMiniProfileFromEmail } from 'Deer/hooks/dataHooks/useGetMiniProfileFromEmail';
import { useUploadProfilePhoto } from 'Deer/hooks/useUploadProfilePhoto';
import { setL2Visible } from 'Deer/slice/DeerNav.slice';
import { ProfileContainer } from 'components/ProfilePage/ProfilePage';
import ProfileSummaryContainer from 'components/ProfilePage/components/ProfileSummaryContainer';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { NoHomePageSupportNetchexMessage } from 'screens/NonAuthenticatedNetchexMessage/NonAuthenticatedNetchexMessage';
import { LevelUpProfilePageContextProvider } from 'screens/Profile/competencySection/context/provider';
import { MeshRecommends } from 'screens/meshRecommends/MeshRecommends';
import { AgendaSidePanelContextProvider } from 'screens/oneOnOne/levelUp/agendaSidePanel/context/provider';
import { LevelUpSidePanelContextProvider } from 'screens/oneOnOne/levelUp/competencySidePanel/context/provider';
import { checkHomePageNudgesEnabled } from 'utils/helpers/featureFlags';

import { ManagerThickOutlined } from 'assets/assetsV1/iconComponents';

import { isNetchexLogin } from 'constants/netChexConstants';

const TeamMember = ({
  profilePhoto,
  displayName,
  subTitle,
  href,
  email,
}: {
  profilePhoto: string;
  displayName: string;
  subTitle: string;
  href: string;
  email: string;
}) => (
  <Link key={email} to={href}>
    <ListItem
      style={{
        width: '100%',
        marginBottom: '4px',
      }}
    >
      <Avatar size="lg">
        <AvatarImage src={profilePhoto} />
        <AvatarFallback>{getInitialsFromName(displayName)}</AvatarFallback>
      </Avatar>
      <Box
        sx={{
          span: {
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
        w="80%"
      >
        <ListItemText title={displayName} subTitle={subTitle} />
      </Box>
    </ListItem>
  </Link>
);

const MeshProfileCard = memo(() => {
  const { user } = useAuthenticatedUser();
  const history = useHistory();
  const role = getProfileRole();

  const managerEmail = user?.manager?.email;

  const { data: managerMiniProfile } = useGetMiniProfileFromEmail({ email: managerEmail });
  const { displayName: managerName } = managerMiniProfile || {};

  const { uploadProfilePhotoHandler } = useUploadProfilePhoto();

  const onFileChange = (event) => {
    uploadProfilePhotoHandler(event.target.files[0]);

    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  const { moduleName } = useOrganisationSettings();

  const applicableL2Modules = getApplicableL2MyDataModules(moduleName);

  return (
    <ProfileCard
      ctaCallback={() => {
        history.push(applicableL2Modules?.[0]?.value);
      }}
      ctaText="View my performance"
      //  TODO: @todo badgeLabel will come from api according to access in L1: Individual|Manager|Admin
      designation={role === 'Admin' ? role : undefined}
      profileImage={user?.profilePhotoSrc}
      userName={user?.displayName}
      showUpdateButton
      onFileChange={onFileChange}
    >
      {user?.designation ? (
        <ProfileCardItem>
          <ProfileCardKey>
            <Flex alignItems="end" marginLeft="2px">
              <Icon IconSVG={Role} />
              <span
                style={{
                  minWidth: '72px',
                }}
              >
                Role:
              </span>
            </Flex>
          </ProfileCardKey>
          <ProfileCardValue>
            <span style={{ margin: '2px 0px 0px 4px' }}>{user?.designation}</span>
          </ProfileCardValue>
        </ProfileCardItem>
      ) : undefined}
      {managerName ? (
        <ProfileCardItem>
          <ProfileCardKey>
            <Flex alignItems="end">
              {/* eslint-disable-next-line react/no-unstable-nested-components */}
              <Icon IconSVG={() => <ManagerThickOutlined fill="transparent" color="icon.g2" boxSize="24px" />} />
              <span
                style={{
                  minWidth: '72px',
                }}
              >
                Reports to:
              </span>
            </Flex>
          </ProfileCardKey>
          <ProfileCardValue>
            {/* removing manager photo as of now as per the uat feedback */}
            {/* <Avatar>
              <AvatarImage src={user?.managerProfilePhoto} />
              <AvatarFallback>{getInitialsFromName(managerName)}</AvatarFallback>
            </Avatar> */}

            <span style={{ paddingLeft: '6px', marginTop: '4px' }}>{managerName}</span>
          </ProfileCardValue>
        </ProfileCardItem>
      ) : undefined}
    </ProfileCard>
  );
});

const TeamCard = memo(() => {
  const { user } = useAuthenticatedUser();
  const { moduleName } = useOrganisationSettings();

  const { data: people = [], isLoading } = usePeople({
    cohortId: STATIC_COHORTS.team.directReports.id,
    module: meshModules.goals.value,
  });

  const peopleWithoutLoggedInUser = people?.filter((person) => person.id !== Number(user?.id));

  if (isLoading || !peopleWithoutLoggedInUser?.length) {
    return null;
  }
  const applicableL2TeamDirectModules = getApplicableL2MyTeamModules('directReports', moduleName);

  return (
    <Box marginTop="12px" width="20rem" overflow="hidden">
      <Card>
        <CardHeader>
          <CardTitle>TEAM MEMBERS ({peopleWithoutLoggedInUser?.length})</CardTitle>
        </CardHeader>
        <CardContent
          style={{
            maxHeight: '180px',
            overflow: 'auto',
          }}
        >
          {peopleWithoutLoggedInUser?.map(({ displayName, email, profilePhoto, employeeFunction, id }) => {
            const href = staticRoutes.team.directReports.profile.replace(':employeeId', String(id));
            return (
              <TeamMember
                key={email}
                profilePhoto={profilePhoto}
                displayName={displayName}
                subTitle={employeeFunction}
                href={href}
                email={email}
              />
            );
          })}
        </CardContent>
        <Divider marginTop="10px" marginLeft="-16px" width="calc(100% + 32px)" />
        <Flex justifyContent="center" width="full" marginBottom="-10px">
          {/** Link would change to overview section once in place. */}
          <Link
            to={`${
              applicableL2TeamDirectModules?.[0]?.value || L3_PATHS.team.directReports.overview.myTeamOverview
            }?source=Home`}
          >
            <Button variant="text" iconRight={ChevronRight}>
              View team performance
            </Button>
          </Link>
        </Flex>
      </Card>
    </Box>
  );
});

const Home = () => {
  const { isDirectManager } = getUserAccess();
  const dispatch = useDispatch();
  const { user } = useAuthenticatedUser();

  const isNetchex = process.env.PUBLIC_NETCHEX_LOGIN_FLOW === 'true';

  // TODO: This is ugly. Refactor opening and closing of sidebars
  useEffect(() => {
    dispatch(
      setL2Visible({
        isL2Visible: false,
      })
    );
  }, [dispatch]);

  if (isNetchexLogin) {
    return <NoHomePageSupportNetchexMessage />;
  }

  return (
    <Grid width="full" minHeight="full" px="96px" py="32px" templateColumns="340px calc(100vw - 690px)" gap="64px">
      <GridItem position="relative">
        <Box position={isNetchex ? 'sticky' : 'fixed'}>
          <MeshProfileCard />
          {isDirectManager ? <TeamCard /> : undefined}
        </Box>
      </GridItem>
      <GridItem>
        {checkHomePageNudgesEnabled() ? (
          <MeshRecommends />
        ) : (
          <LevelUpSidePanelContextProvider>
            <AgendaSidePanelContextProvider>
              <LevelUpProfilePageContextProvider employeeId={user?.id}>
                <ProfileContainer>
                  <ProfileSummaryContainer employeeId={user?.id} />
                </ProfileContainer>
              </LevelUpProfilePageContextProvider>
            </AgendaSidePanelContextProvider>
          </LevelUpSidePanelContextProvider>
        )}
      </GridItem>
    </Grid>
  );
};

export default Home;
